/**
 * FillLightChaptersDialog.jsx
 *
 * Bulk-expand chapters that fall below their target word count.
 *
 * Flow:
 *   1. Scan  → backend lists chapters below threshold
 *   2. Select → user ticks which chapters to expand
 *   3. Run    → SSE stream expands each chapter; live progress shown
 *   4. Review → per-chapter Apply / Discard buttons
 */
import React, { useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Zap, CheckCircle2, XCircle, BarChart3, BookOpen, Eye, Pencil } from 'lucide-react';
import { cn } from '@/lib/utils';
import { streamSSE } from '@/lib/auth_api';
import { usePW } from '@/lib/store';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

// ── paragraph-level diff ─────────────────────────────────────────────────────
function ParagraphDiff({ original, expanded }) {
  const origParas = (original || '').split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
  const expParas  = (expanded  || '').split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
  const origSet   = new Set(origParas);

  return (
    <ScrollArea className="max-h-[360px] rounded-md border border-border/60 bg-muted/30 p-3">
      <div className="space-y-2 text-[12.5px] leading-relaxed">
        {expParas.map((para, i) => {
          const isNew = !origSet.has(para);
          return (
            <p
              key={i}
              className={cn(
                'rounded px-2 py-1 transition-colors',
                isNew
                  ? 'bg-emerald-500/10 text-emerald-900 dark:text-emerald-100 ring-1 ring-inset ring-emerald-500/20'
                  : 'text-foreground/80',
              )}
              title={isNew ? 'New paragraph added by AI' : 'Original paragraph'}
            >
              {isNew && (
                <span className="mr-1.5 inline-flex items-center rounded-sm bg-emerald-500/20 px-1 py-px text-[9px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
                  new
                </span>
              )}
              {para}
            </p>
          );
        })}
      </div>
    </ScrollArea>
  );
}

// ── word count badge colour ────────────────────────────────────────────────
function WcBadge({ current, target }) {
  const pct = Math.min(100, Math.round((current / Math.max(1, target)) * 100));
  const col = pct >= 85 ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400'
            : pct >= 50 ? 'bg-amber-500/15 text-amber-700 dark:text-amber-400'
            :             'bg-rose-500/15 text-rose-700 dark:text-rose-400';
  return (
    <span className={cn('rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums', col)}>
      {current.toLocaleString()} / {target.toLocaleString()}
    </span>
  );
}

// ── chapter row ─────────────────────────────────────────────────────────────
function ChapterRow({ ch, selected, onToggle, status, newWords, onTargetChange }) {
  const [editingTarget, setEditingTarget] = useState(false);
  const [localTarget, setLocalTarget] = useState(String(ch.target_words));
  const pct = Math.min(100, Math.round((ch.current_words / Math.max(1, ch.target_words)) * 100));
  const statusIcon = status === 'running' ? <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
                   : status === 'done'    ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                   : status === 'error'   ? <XCircle className="h-3.5 w-3.5 text-rose-500" />
                   : null;

  const commitTarget = () => {
    const n = parseInt(localTarget, 10);
    if (!isNaN(n) && n >= 100 && n <= 20000) {
      onTargetChange?.(ch.id, n);
    } else {
      setLocalTarget(String(ch.target_words));
    }
    setEditingTarget(false);
  };

  return (
    <div
      className={cn(
        'group flex items-start gap-3 rounded-lg border border-border/60 px-3 py-2.5 transition-colors',
        selected && 'border-primary/30 bg-primary/5',
        status === 'done' && 'border-emerald-500/30 bg-emerald-500/5',
        status === 'error' && 'border-rose-500/30 bg-rose-500/5',
      )}
      data-testid={`fill-light-row-${ch.id}`}
    >
      <Checkbox
        checked={selected}
        onCheckedChange={onToggle}
        disabled={status === 'running' || status === 'done'}
        data-testid={`fill-light-check-${ch.id}`}
        className="mt-0.5 shrink-0"
      />
      <div className="flex flex-1 flex-col gap-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider shrink-0">
            Ch {ch.number}
          </span>
          <span className="truncate text-[13px] font-medium text-foreground">
            {ch.title || '(untitled)'}
          </span>
          {statusIcon && <span className="ml-auto shrink-0">{statusIcon}</span>}
        </div>
        <div className="flex items-center gap-2">
          <Progress value={pct} className="h-1.5 flex-1" />
          <WcBadge current={ch.current_words} target={ch.target_words} />
          {/* Per-chapter target editor */}
          {!status && onToggle && (
            editingTarget ? (
              <Input
                type="number"
                value={localTarget}
                min={100}
                max={20000}
                className="h-5 w-20 text-[11px] px-1"
                autoFocus
                onChange={(e) => setLocalTarget(e.target.value)}
                onBlur={commitTarget}
                onKeyDown={(e) => { if (e.key === 'Enter') commitTarget(); if (e.key === 'Escape') { setLocalTarget(String(ch.target_words)); setEditingTarget(false); } }}
                onClick={(e) => e.stopPropagation()}
                data-testid={`fill-light-target-input-${ch.id}`}
              />
            ) : (
              <button
                className="shrink-0 rounded p-0.5 opacity-0 transition-opacity hover:opacity-100 focus:opacity-100 group-hover:opacity-60"
                title="Edit target word count for this chapter"
                onClick={(e) => { e.stopPropagation(); setEditingTarget(true); }}
                data-testid={`fill-light-target-edit-${ch.id}`}
              >
                <Pencil className="h-2.5 w-2.5 text-muted-foreground" />
              </button>
            )
          )}
        </div>
        {status === 'done' && newWords != null && (
          <p className="text-[11px] text-emerald-600 dark:text-emerald-400">
            Expanded to {newWords.toLocaleString()} words (+{(newWords - ch.current_words).toLocaleString()})
          </p>
        )}
        {status === 'error' && (
          <p className="text-[11px] text-rose-600 dark:text-rose-400">Expansion failed — skipped.</p>
        )}
      </div>
    </div>
  );
}

// ── main dialog ──────────────────────────────────────────────────────────────
export function FillLightChaptersDialog({ open, onOpenChange }) {
  const { project, updateChapter, chapters: storeChapters } = usePW();

  // scanning
  const [scanning, setScanning]   = useState(false);
  const [lightChaps, setLightChaps] = useState(null);

  // selection
  const [selected, setSelected]   = useState(new Set());

  // per-chapter target overrides (saved to backend)
  const [targets, setTargets]     = useState({});   // { chapterId: number }

  // running
  const [phase, setPhase]         = useState('idle');
  const [chStatus, setChStatus]   = useState({});
  const [chNewWords, setChNewWords] = useState({});
  const [expanded, setExpanded]   = useState({});
  const [applied, setApplied]     = useState(new Set());
  const [runProgress, setRunProgress] = useState(0);

  // diff preview
  const [diffCid, setDiffCid]     = useState(null);   // which chapter is being previewed

  const abortRef = useRef(null); // kept for future abort support

  const reset = () => {
    setScanning(false);
    setLightChaps(null);
    setSelected(new Set());
    setTargets({});
    setPhase('idle');
    setChStatus({});
    setChNewWords({});
    setExpanded({});
    setApplied(new Set());
    setRunProgress(0);
    setDiffCid(null);
  };

  // ── per-chapter target ──────────────────────────────────────────────────
  const handleTargetChange = useCallback(async (cid, newTarget) => {
    setTargets((t) => ({ ...t, [cid]: newTarget }));
    setLightChaps((prev) => prev
      ? prev.map((c) => c.id === cid ? { ...c, target_words: newTarget, deficit: Math.max(0, newTarget - c.current_words) } : c)
      : prev
    );
    try {
      await axios.patch(`${API}/chapters/${cid}/word-count-target`, { target_words: newTarget }, { withCredentials: true });
      if (updateChapter) updateChapter(cid, { target_words: newTarget });
    } catch { toast.error('Could not save target word count.'); }
  }, [updateChapter]);

  // ── scan ────────────────────────────────────────────────────────────────
  const handleScan = async () => {
    if (!project?.id) { toast.error('No project loaded.'); return; }
    setScanning(true);
    setPhase('scanning');
    try {
      const r = await axios.post(
        `${API}/projects/${project.id}/fill-light/scan`,
        { threshold: 0.85 },
        { withCredentials: true },
      );
      const chaps = r.data.chapters || [];
      setLightChaps(chaps);
      setSelected(new Set(chaps.map((c) => c.id)));
      setPhase(chaps.length === 0 ? 'idle' : 'selecting');
      if (chaps.length === 0) {
        toast.success('All chapters are at or above their target word count.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Scan failed — please try again.');
      setPhase('idle');
    } finally {
      setScanning(false);
    }
  };

  // ── run ─────────────────────────────────────────────────────────────────
  const handleRun = useCallback(async () => {
    if (!project?.id || selected.size === 0) return;
    setPhase('running');
    setChStatus({});
    setChNewWords({});
    setExpanded({});
    setApplied(new Set());
    setRunProgress(0);

    const ids = [...selected];
    let done = 0;

    try {
      await streamSSE(
        `/stream/projects/${project.id}/fill-light/run`,
        { chapter_ids: ids },
        {
          onEvent: ({ event, data }) => {
            if (event === 'chapter_start') {
              setChStatus((s) => ({ ...s, [data.chapter_id]: 'running' }));
            } else if (event === 'delta') {
              setExpanded((s) => ({
                ...s,
                [data.chapter_id]: (s[data.chapter_id] || '') + data.text,
              }));
            } else if (event === 'chapter_done') {
              done += 1;
              setChStatus((s) => ({ ...s, [data.chapter_id]: 'done' }));
              setChNewWords((s) => ({ ...s, [data.chapter_id]: data.new_words }));
              setExpanded((s) => ({ ...s, [data.chapter_id]: data.expanded_text }));
              setRunProgress(Math.round((done / ids.length) * 100));
            } else if (event === 'error') {
              done += 1;
              setChStatus((s) => ({ ...s, [data.chapter_id]: 'error' }));
              setRunProgress(Math.round((done / ids.length) * 100));
            } else if (event === 'all_done') {
              setPhase('done');
              setRunProgress(100);
            }
          },
          onError: () => {
            toast.error('Stream error — some chapters may still have been expanded.');
            setPhase('done');
          },
          onClose: () => {
            setPhase((p) => p === 'running' ? 'done' : p);
            setRunProgress(100);
          },
        },
      );
    } catch (err) {
      toast.error('Expansion failed. Please try again.');
      setPhase('done');
    }
  }, [project, selected]);

  // ── apply / discard ──────────────────────────────────────────────────────
  const applyChapter = async (cid) => {
    const text = expanded[cid];
    if (!text || !project?.id) return;
    try {
      await axios.put(
        `${API}/chapters/${cid}`,
        { content: text },
        { withCredentials: true },
      );
      if (updateChapter) updateChapter(cid, { content: text });
      setApplied((s) => new Set([...s, cid]));
      toast.success('Chapter updated.');
    } catch {
      toast.error('Failed to save chapter.');
    }
  };

  const applyAll = async () => {
    const doneIds = Object.entries(chStatus)
      .filter(([, s]) => s === 'done')
      .map(([id]) => id);
    for (const cid of doneIds) {
      if (!applied.has(cid)) await applyChapter(cid);
    }
  };

  // ── render ───────────────────────────────────────────────────────────────
  const allToggle = () => {
    if (selected.size === lightChaps.length) setSelected(new Set());
    else setSelected(new Set(lightChaps.map((c) => c.id)));
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent
        className="max-w-xl"
        data-testid="fill-light-chapters-dialog"
      >
        <DialogHeader className="border-b border-border pb-3">
          <DialogTitle className="flex items-center gap-2 text-[14px]">
            <Zap className="h-4 w-4 text-amber-500" />
            Fill Light Chapters
          </DialogTitle>
          <DialogDescription className="text-[12px]">
            Scan for chapters below their target word count, then let Claude expand
            them — preserving every existing beat, voice, and story decision.
          </DialogDescription>
        </DialogHeader>

        {/* ── idle / scan prompt ─────────────────────────────────────── */}
        {phase === 'idle' && (
          <div className="flex flex-col items-center gap-4 py-8 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-amber-500/10">
              <BarChart3 className="h-7 w-7 text-amber-500" />
            </div>
            <div>
              <p className="text-[13.5px] font-medium text-foreground">
                Find chapters that need more depth
              </p>
              <p className="mt-1 text-[12px] text-muted-foreground">
                Any chapter below 85% of your target word count will be flagged for expansion.
              </p>
            </div>
            <Button
              onClick={handleScan}
              disabled={scanning || !project?.id}
              data-testid="fill-light-scan-btn"
              className="gap-2"
            >
              {scanning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <BookOpen className="h-3.5 w-3.5" />}
              {scanning ? 'Scanning…' : 'Scan Manuscript'}
            </Button>
          </div>
        )}

        {/* ── selecting ──────────────────────────────────────────────── */}
        {phase === 'selecting' && lightChaps && (
          <>
            <div className="flex items-center justify-between py-2">
              <p className="text-[12px] text-muted-foreground">
                {lightChaps.length} chapter{lightChaps.length !== 1 ? 's' : ''} below target
                &nbsp;·&nbsp;
                <button
                  className="underline underline-offset-2 hover:text-foreground"
                  onClick={allToggle}
                  data-testid="fill-light-toggle-all"
                >
                  {selected.size === lightChaps.length ? 'Deselect all' : 'Select all'}
                </button>
              </p>
              <Badge variant="secondary" className="text-[11px]">
                {selected.size} selected
              </Badge>
            </div>
            <ScrollArea className="max-h-[320px] pr-3">
              <div className="flex flex-col gap-2 pb-1">
                {lightChaps.map((ch) => (
                  <ChapterRow
                    key={ch.id}
                    ch={ch}
                    selected={selected.has(ch.id)}
                    onToggle={() => setSelected((s) => {
                      const n = new Set(s);
                      n.has(ch.id) ? n.delete(ch.id) : n.add(ch.id);
                      return n;
                    })}
                    status={null}
                    newWords={null}
                    onTargetChange={handleTargetChange}
                  />
                ))}
              </div>
            </ScrollArea>
            <DialogFooter className="border-t border-border pt-3">
              <Button variant="ghost" size="sm" onClick={reset}>Cancel</Button>
              <Button
                size="sm"
                onClick={handleRun}
                disabled={selected.size === 0}
                data-testid="fill-light-run-btn"
                className="gap-2"
              >
                <Zap className="h-3.5 w-3.5" />
                Expand {selected.size} chapter{selected.size !== 1 ? 's' : ''}
              </Button>
            </DialogFooter>
          </>
        )}

        {/* ── running ────────────────────────────────────────────────── */}
        {phase === 'running' && lightChaps && (
          <>
            <div className="py-2">
              <div className="mb-3 flex items-center justify-between">
                <p className="text-[12px] font-medium text-foreground">Expanding chapters…</p>
                <span className="text-[12px] text-muted-foreground">{runProgress}%</span>
              </div>
              <Progress value={runProgress} className="mb-4 h-2" data-testid="fill-light-progress" />
            </div>
            <ScrollArea className="max-h-[320px] pr-3">
              <div className="flex flex-col gap-2 pb-1">
                {lightChaps.filter((c) => selected.has(c.id)).map((ch) => (
                  <ChapterRow
                    key={ch.id}
                    ch={ch}
                    selected
                    onToggle={() => {}}
                    status={chStatus[ch.id] || 'queued'}
                    newWords={chNewWords[ch.id]}
                  />
                ))}
              </div>
            </ScrollArea>
          </>
        )}

        {/* ── done ───────────────────────────────────────────────────── */}
        {phase === 'done' && lightChaps && (
          <>
            <div className="py-2">
              <p className="mb-3 text-[12px] text-muted-foreground">
                Review each chapter and apply the expansions you want to keep.
              </p>
            </div>
            <ScrollArea className="max-h-[320px] pr-3">
              <div className="flex flex-col gap-2 pb-1">
                {lightChaps.filter((c) => selected.has(c.id)).map((ch) => (
                  <div key={ch.id} className="flex flex-col gap-1.5">
                    <ChapterRow
                      ch={ch}
                      selected
                      onToggle={() => {}}
                      status={chStatus[ch.id]}
                      newWords={chNewWords[ch.id]}
                    />
                    {chStatus[ch.id] === 'done' && !applied.has(ch.id) && (
                      <div className="flex flex-col gap-2 pl-7">
                        {/* Diff preview toggle */}
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 justify-start gap-1.5 text-[12px] text-muted-foreground hover:text-foreground"
                          onClick={() => setDiffCid(diffCid === ch.id ? null : ch.id)}
                          data-testid={`fill-light-preview-${ch.id}`}
                        >
                          <Eye className="h-3 w-3" />
                          {diffCid === ch.id ? 'Hide preview' : 'Preview changes'}
                        </Button>
                        {diffCid === ch.id && (
                          <ParagraphDiff
                            original={(storeChapters || []).find((c) => c.id === ch.id)?.content || ''}
                            expanded={expanded[ch.id] || ''}
                          />
                        )}
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 gap-1.5 text-[12px]"
                            onClick={() => applyChapter(ch.id)}
                            data-testid={`fill-light-apply-${ch.id}`}
                          >
                            <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                            Apply
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 gap-1 text-[12px] text-muted-foreground"
                            data-testid={`fill-light-discard-${ch.id}`}
                            onClick={() => setChStatus((s) => ({ ...s, [ch.id]: 'discarded' }))}
                          >
                            <XCircle className="h-3 w-3" />
                            Discard
                          </Button>
                        </div>
                      </div>
                    )}
                    {applied.has(ch.id) && (
                      <p className="pl-7 text-[11px] text-emerald-600 dark:text-emerald-400">Applied to manuscript.</p>
                    )}
                  </div>
                ))}
              </div>
            </ScrollArea>
            <DialogFooter className="border-t border-border pt-3 gap-2">
              <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>Close</Button>
              <Button
                size="sm"
                onClick={applyAll}
                data-testid="fill-light-apply-all-btn"
                className="gap-1.5"
              >
                <CheckCircle2 className="h-3.5 w-3.5" />
                Apply All
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default FillLightChaptersDialog;
